'use client'
import { logger } from '@/lib/logger'
import { getRuntimePublicConfigValue } from '@/src/lib/runtime-public-config'

import { useEffect, useRef } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'

type PostHogModule = typeof import('posthog-js')
type PostHogClient = PostHogModule['default']

const IS_DEV = process.env.NODE_ENV === 'development'

function getPosthogKey(): string | undefined {
  return (
    getRuntimePublicConfigValue('NEXT_PUBLIC_POSTHOG_KEY') ||
    process.env.NEXT_PUBLIC_POSTHOG_KEY
  )
}

function getPosthogHost(): string {
  return (
    getRuntimePublicConfigValue('NEXT_PUBLIC_POSTHOG_HOST') ||
    process.env.NEXT_PUBLIC_POSTHOG_HOST ||
    'https://us.i.posthog.com'
  )
}

function getPublicEnv(): string {
  return (
    getRuntimePublicConfigValue('NEXT_PUBLIC_ENV') ||
    process.env.NEXT_PUBLIC_ENV ||
    'development'
  )
}

let initPromise: Promise<PostHogClient | null> | null = null
let posthogClient: PostHogClient | null = null
const pendingOperations: Array<(client: PostHogClient) => void> = []

function flushPendingOperations(client: PostHogClient) {
  while (pendingOperations.length > 0) {
    const operation = pendingOperations.shift()
    operation?.(client)
  }
}

function enqueueOperation(operation: (client: PostHogClient) => void) {
  pendingOperations.push(operation)
}

function withPostHogClient(operation: (client: PostHogClient) => void) {
  if (posthogClient) {
    operation(posthogClient)
    return
  }

  enqueueOperation(operation)
}

// Keep PostHog off the critical path and flush queued calls once it loads.
async function initPostHog() {
  const posthogKey = getPosthogKey()
  if (!posthogKey || typeof window === 'undefined') {
    return null
  }

  if (posthogClient) {
    return posthogClient
  }

  if (initPromise) {
    return initPromise
  }

  initPromise = import('posthog-js')
    .then(({ default: posthog }) => {
      const env = getPublicEnv()
      const isProduction = env === 'production'
      const isStaging = env === 'staging'

      posthog.init(posthogKey, {
        api_host: getPosthogHost(),

        // Don't auto-capture pageviews - we handle this manually for Next.js
        capture_pageview: false,
        capture_pageleave: true,

        // Session recording
        // Production: Enabled, Staging: Enabled, Dev: Disabled
        disable_session_recording: IS_DEV,

        // Autocapture clicks, form submissions, etc.
        autocapture: !IS_DEV,

        // Persistence
        persistence: 'localStorage+cookie',

        // Privacy settings
        mask_all_text: isProduction, // Mask sensitive text in production
        mask_all_element_attributes: false,

        // Bootstrap for faster load
        bootstrap: {
          distinctID: undefined, // Will be set on identify
        },

        // Feature flags
        advanced_disable_feature_flags: IS_DEV,

        // Reduce noise in development
        opt_out_capturing_by_default: IS_DEV,

        // Debug mode in non-production
        debug: !isProduction && !isStaging,

        // Loaded callback
        loaded: (ph) => {
          // Expose PostHog globally for debugging in non-production
          if (!isProduction) {
            ;(window as unknown as { posthog: PostHogClient }).posthog = ph
          }
        },
      })

      posthog.register({
        app: 'dday-web',
        environment: env,
      })

      posthogClient = posthog
      flushPendingOperations(posthog)
      return posthog
    })
    .catch((error) => {
      initPromise = null
      logger.error('[PostHog] Failed to initialize:', error)
      return null
    })

  return initPromise
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const lastPathRef = useRef<string | null>(null)

  useEffect(() => {
    const INTERACTION_EVENTS = [
      'pointerdown',
      'keydown',
      'scroll',
      'touchstart',
    ]

    let scheduled = false

    const scheduleInit = () => {
      if (scheduled) return
      scheduled = true
      cleanup()
      if ('requestIdleCallback' in window) {
        requestIdleCallback(
          () => {
            void initPostHog()
          },
          { timeout: 3000 }
        )
      } else {
        setTimeout(() => {
          void initPostHog()
        }, 3000)
      }
    }

    // Passive visitors still need analytics even if they never interact.
    const hardDeadline = setTimeout(scheduleInit, 5000)

    const cleanup = () => {
      clearTimeout(hardDeadline)
      INTERACTION_EVENTS.forEach((e) =>
        window.removeEventListener(e, scheduleInit, { capture: true })
      )
    }

    INTERACTION_EVENTS.forEach((e) =>
      window.addEventListener(e, scheduleInit, {
        once: true,
        capture: true,
        passive: true,
      })
    )

    return cleanup
  }, [])

  useEffect(() => {
    if (!pathname) return

    const url = searchParams?.toString()
      ? `${pathname}?${searchParams.toString()}`
      : pathname

    if (lastPathRef.current === url) return
    lastPathRef.current = url

    withPostHogClient((client) => {
      client.capture('$pageview', {
        $current_url:
          typeof window !== 'undefined' ? window.location.href : url,
      })
    })
  }, [pathname, searchParams])

  return <>{children}</>
}

/**
 * Identify a user in PostHog (call after login)
 */
export function identifyUser(
  userId: string,
  properties?: Record<string, unknown>
) {
  const env = getPublicEnv()
  const posthogKey = getPosthogKey()
  const props = { ...properties, app: 'dday-web', environment: env }
  if (!posthogKey || IS_DEV) {
    console.log(`[Analytics] identify`, { userId, ...props })
    return
  }
  withPostHogClient((client) => {
    client.identify(userId, props)
  })
}

/**
 * Reset user identity (call on logout)
 */
export function resetUser() {
  const posthogKey = getPosthogKey()
  if (!posthogKey || IS_DEV) {
    console.log(`[Analytics] reset`)
    return
  }
  withPostHogClient((client) => {
    client.reset()
  })
}

/**
 * Track a custom event
 * - Dev: logs to console with [Analytics] prefix
 * - Staging/Prod: sends to PostHog with environment tag
 */
export function trackEvent(
  eventName: string,
  properties?: Record<string, unknown>
) {
  const env = getPublicEnv()
  const posthogKey = getPosthogKey()
  const props = { ...properties, app: 'dday-web', environment: env }
  if (!posthogKey || IS_DEV) {
    console.log(`[Analytics] ${eventName}`, props)
    return
  }
  withPostHogClient((client) => {
    client.capture(eventName, props)
  })
}

/**
 * Utility: Check if a feature flag is enabled
 */
export function isFeatureEnabled(flagKey: string): boolean {
  const posthogKey = getPosthogKey()
  if (!posthogKey || IS_DEV) return false

  return posthogClient?.isFeatureEnabled(flagKey) ?? false
}

/**
 * Utility: Get feature flag value (for multivariate flags)
 */
export function getFeatureFlagValue(
  flagKey: string
): string | boolean | undefined {
  const posthogKey = getPosthogKey()
  if (!posthogKey || IS_DEV) return undefined

  return posthogClient?.getFeatureFlag(flagKey)
}
